// What's playing from the media library. Audio plays in a persistent bar (full screen on
// phones when opened); video opens a player overlay. Both survive navigation between
// channels. Playing from a list queues the rest of it (HRTH-164.1); the lock screen and
// media keys drive it through the Media Session API where the browser has one.
import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'
import type { LibraryRef, PlayResponse } from '../api/types'
import * as Q from '../lib/queue'
import { useParty } from './party'
import { useSession } from './session'

export interface NowPlaying {
  item: LibraryRef
  play: PlayResponse
}

export const usePlayer = defineStore('player', () => {
  const current = ref<NowPlaying | null>(null)
  const error = ref<string | null>(null)
  const queue = ref<Q.Queue>({ items: [], at: 0 })
  /** The playing <audio>/<video>, set by the player that shows it. */
  const el = shallowRef<HTMLMediaElement | null>(null)
  /** Cover art of what's playing (an object URL), when it has some. */
  const art = ref<string | null>(null)
  /** Phones: Now Playing open full screen. */
  const expanded = ref(false)
  let loading = 0

  const hasNext = computed(() => Q.hasNext(queue.value))
  const hasPrevious = computed(() => Q.hasPrevious(queue.value))
  const upNext = computed(() => Q.upNext(queue.value))

  async function load(q: Q.Queue): Promise<void> {
    const item = q.items[q.at]
    if (!item) return
    const mine = ++loading
    error.value = null
    queue.value = q
    // One thing plays at a time: playing for yourself leaves the room (joining one stops this).
    const party = useParty()
    if (party.joined) void party.leave()
    const session = useSession()
    try {
      const p = await session.playLibrary(item)
      if (mine !== loading) return
      current.value = { item, play: p }
      void loadArt(item, mine)
    } catch (e) {
      if (mine !== loading) return
      error.value = (e as Error).message
      session.toast(`Can't play ${item.title}: ${(e as Error).message}`, 'error')
    }
  }

  async function loadArt(item: LibraryRef, mine: number): Promise<void> {
    art.value = null
    const session = useSession()
    const { refArt } = await import('../lib/libraryPlay')
    const url = await refArt(session.api, session.libraryKey(item.library), item).catch(() => null)
    if (mine === loading) art.value = url
  }

  /** Plays `item`; from a list (album, folder, results), the rest of it is queued after it. */
  function play(item: LibraryRef, list?: LibraryRef[], opts: { shuffle?: boolean } = {}): Promise<void> {
    return load(Q.startQueue(item, list, opts.shuffle))
  }

  function next(): Promise<void> {
    const q = Q.next(queue.value)
    return q ? load(q) : Promise.resolve()
  }

  function previous(): Promise<void> {
    const q = Q.previous(queue.value, el.value?.currentTime ?? 0)
    if (q !== 'restart') return load(q)
    if (el.value) el.value.currentTime = 0
    return Promise.resolve()
  }

  /** Plays the queue's `n`th item (from Up next). */
  function jump(n: number): Promise<void> {
    return n >= 0 && n < queue.value.items.length ? load({ ...queue.value, at: n }) : Promise.resolve()
  }

  /** Right after the one playing; plays now if nothing is. */
  function playNext(item: LibraryRef): Promise<void> {
    if (!current.value) return play(item)
    queue.value = Q.playNext(queue.value, item)
    return Promise.resolve()
  }

  /** At the end of the queue; plays now if nothing is. */
  function enqueue(item: LibraryRef): Promise<void> {
    if (!current.value) return play(item)
    queue.value = Q.enqueue(queue.value, item)
    return Promise.resolve()
  }

  /** The playing item ended: on to the next, if there is one. */
  function ended(): void {
    if (hasNext.value) void next()
  }

  function stop(): void {
    loading++
    current.value = null
    queue.value = { items: [], at: 0 }
    art.value = null
    expanded.value = false
  }

  // --- Media Session: lock screen, notification and media key controls.
  const ms = typeof navigator !== 'undefined' && 'mediaSession' in navigator ? navigator.mediaSession : null
  function handle(action: MediaSessionAction, fn: MediaSessionActionHandler | null): void {
    try {
      ms?.setActionHandler(action, fn)
    } catch {
      // not supported by this browser
    }
  }
  watch([current, art], ([c, a]) => {
    if (!ms) return
    if (!c) {
      ms.metadata = null
      for (const x of ['play', 'pause', 'previoustrack', 'nexttrack', 'seekto'] as const) handle(x, null)
      return
    }
    ms.metadata = new MediaMetadata({
      title: c.item.title,
      artist: c.item.artist ?? '',
      ...(a ? { artwork: [{ src: a }] } : {}),
    })
    handle('play', () => void el.value?.play().catch(() => undefined))
    handle('pause', () => el.value?.pause())
    handle('previoustrack', () => void previous())
    handle('nexttrack', hasNext.value ? () => void next() : null)
    handle('seekto', (d) => {
      if (el.value && d.seekTime !== undefined) el.value.currentTime = d.seekTime
    })
  })
  watch(hasNext, (n) => current.value && handle('nexttrack', n ? () => void next() : null))

  /** Keeps the lock screen's position in step (called on timeupdate). */
  function position(): void {
    const m = el.value
    if (!ms?.setPositionState || !m || !Number.isFinite(m.duration) || m.duration <= 0) return
    try {
      ms.setPositionState({ duration: m.duration, position: Math.min(m.currentTime, m.duration), playbackRate: m.playbackRate || 1 })
    } catch {
      // a position the browser refuses (e.g. mid-load)
    }
  }

  return { current, error, queue, el, art, expanded, hasNext, hasPrevious, upNext, play, next, previous, jump, playNext, enqueue, ended, stop, position }
})
